import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { Loader2, Plus } from 'lucide-react';
import useRerender from '../../Common/components/hooks/useRerender';
import Table, { createColumnHelper } from '../../Common/components/Table';
import AccountActions from './AccountActions';
import { Button } from '@/src/shadComponents/ui/button';
import useAccounts from './hooks/useAccounts';
import PageShell from '@/src/Common/components/PageShell';

type Account = {
    username: string;
    email: string;
    id: number;
    firstName: string;
    lastName: string;
};

const columnHelper = createColumnHelper<Account>();

export default function Accounts() {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [accounts, setAccounts] = useState<Account[]>([]);
    const { getAccounts } = useAccounts();

    const { render, renderCount } = useRerender();

    const onDelete = () => {
        render();
    };

    const columnDefinitions = [
        columnHelper.accessor('username', {
            id: 'username',
            header: () => <span>username</span>,
            cell: (info) => <span>{info.getValue()}</span>,
        }),
        columnHelper.accessor('email', {
            id: 'email',
            header: () => <span>email</span>,
            cell: (info) => <span>{info.getValue()}</span>,
        }),
        columnHelper.accessor('firstName', {
            id: 'firstName',
            header: () => <span>first name</span>,
            cell: (info) => <span>{info.getValue()}</span>,
        }),
        columnHelper.accessor('lastName', {
            id: 'lastName',
            header: () => <span>last name</span>,
            cell: (info) => <span>{info.getValue()}</span>,
        }),
        columnHelper.display({
            id: 'actions',
            cell: (props: { row: any }) => (
                <AccountActions onDelete={onDelete} row={props.row} />
            ),
        }),
    ];

    useEffect(() => {
        let isMounted = true;
        const fetchData = async () => {
            try {
                const response = await getAccounts();
                // @ts-ignore
                setAccounts(response);
            } catch (error) {
                console.error(error);
            } finally {
                isMounted && setIsLoading(false);
            }
        };

        fetchData();

        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [renderCount]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="size-6 animate-spin text-muted-foreground" />
            </div>
        );
    }

    const actions = (
        <NavLink to="create">
            <Button>
                <Plus className="size-4" />
                Create Account
            </Button>
        </NavLink>
    );

    return (
        <PageShell title="Accounts" breadcrumbs={[{ label: 'Accounts' }]} actions={actions}>
            <Table data={accounts} columns={columnDefinitions} />
        </PageShell>
    );
}
